import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { NoteApi } from "../../lib/noteUpload";
import { type RecorderDone, VideoRecorder } from "./VideoRecorder";

/** The recorder as a dark dialog over the app, for the host (a sheet from below on a phone, `layout` says). The guest's page draws the same recorder in its own sheet. */
export function RecorderDialog({ limitSec, done, onClose, layout, upload }: { limitSec: number; done: RecorderDone; onClose: () => void; layout?: "phone" | "upright" | "side"; upload?: NoteApi }): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    box.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.stopPropagation();
      close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return createPortal(
    <div className="scrim" data-lay={layout} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} className="vr-dialog" role="dialog" aria-modal="true" aria-label="Record a video note" tabIndex={-1}>
        <VideoRecorder limitSec={limitSec} done={done} onClose={onClose} upload={upload} />
      </div>
    </div>,
    document.body,
  );
}
